<script lang="ts" setup>
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import PodDetailsCard from "@/views/trips/components/PodDetailsCard.vue";
import { useFreights } from "@/composables";
import { OrderStopType } from "@/constants/order";
import { getEntityKey } from "@/utils/sameEntity";
import type { Bol, RouteStopDto, TripRouteDto } from "@/api/trips/dto/trip.dto";
import type { PodDetailsGroup, TripRouteFreight, TripRouteStop } from "@/types/trip";

const { t } = useI18n();
const { getFreightName } = useFreights();

const bols = defineModel<Bol[]>({ default: () => [] });

const props = withDefaults(
  defineProps<{
    route: TripRouteDto;
    stop: RouteStopDto;
    readonly?: boolean;
  }>(),
  {
    readonly: false,
  },
);

const routeStops = computed<(TripRouteStop | RouteStopDto)[]>(() => props.route.routeStops);

const stopBolsById = computed<Map<string, Bol>>(() => {
  return new Map(props.stop.bols.map((bol) => [bol.id, bol]));
});

const stopFreights = computed<TripRouteFreight[]>(() => {
  return props.stop.freights
    .map((freightId) => props.route.freights.find((freight) => getEntityKey(freight) === freightId))
    .filter((freight) => freight !== undefined)
    .map((freight, index) => ({
      ...freight,
      number: index + 1,
      name: getFreightName(freight, index + 1, "Freight", false, !freight.quantity),
    }));
});

const usedBolIds = computed<string[]>(() => {
  return [...new Set(stopFreights.value.flatMap((freight) => getStopBolIds(freight)))];
});

const bolGroups = computed<PodDetailsGroup[]>(() => {
  const groups = new Map<string, PodDetailsGroup>(
    bols.value.map((bol) => [bol.id, { id: bol.id, bol, pickUp: findStop(bol.routeStopId), freights: [] }]),
  );

  stopFreights.value.forEach((freight) => {
    getStopBolIds(freight).forEach((bolId) => groups.get(bolId)?.freights.push(freight));
  });

  return [...groups.values()];
});

const freightsWithoutBolGroups = computed<PodDetailsGroup[]>(() => {
  const groups = new Map<string, PodDetailsGroup>();

  stopFreights.value
    .filter((freight) => !getStopBolIds(freight).length)
    .forEach((freight) => {
      const pickUp = findFreightPickUp(getEntityKey(freight));
      const groupId = String(pickUp?.id ?? "");
      const group = groups.get(groupId) ?? { id: groupId, bol: null, pickUp, freights: [] };

      group.freights.push(freight);
      groups.set(groupId, group);
    });

  return [...groups.values()];
});

function getStopBolIds(freight: TripRouteFreight): string[] {
  return freight.bolIds.filter((bolId) => stopBolsById.value.has(bolId));
}

function findStop(stopId: number | undefined): TripRouteStop | RouteStopDto | null {
  if (!stopId) {
    return null;
  }

  return routeStops.value.find((stop) => stop.id === stopId) ?? null;
}

function findFreightPickUp(freightId: string): TripRouteStop | RouteStopDto | null {
  return (
    routeStops.value.find((stop) => stop.type !== OrderStopType.delivery && stop.freights.includes(freightId)) ?? null
  );
}

function getSignedBy(bol: Bol | null): string {
  if (!bol) {
    return "";
  }

  return bol.signedBy[props.stop.id!] ?? "";
}

function updateSignedBy(bol: Bol | null, name: string): void {
  if (!bol) {
    return;
  }

  bols.value = bols.value.map((item) => {
    return item.id === bol.id ? { ...item, signedBy: { ...item.signedBy, [props.stop.id!]: name } } : item;
  });
}

watch(
  () => usedBolIds.value.join(),
  () => {
    bols.value = usedBolIds.value
      .map((bolId) => stopBolsById.value.get(bolId))
      .filter((bol) => bol !== undefined)
      .map((bol) => ({ ...bol, signedBy: { ...bol.signedBy } }));
  },
  { immediate: true },
);
</script>

<template>
  <div class="d-flex flex-column row-gap-6">
    <div v-if="bols.length">
      <div class="font-weight-500 mb-4 text-grey-navy">{{ t("trip.pod-details") }}</div>

      <div class="d-flex flex-column row-gap-4" data-qa="pod-bol-cards">
        <pod-details-card
          v-for="group in bolGroups"
          :key="group.id"
          :group="group"
          :readonly="readonly"
          :signed-by="getSignedBy(group.bol)"
          :stop="stop"
          @update:signed-by="updateSignedBy(group.bol, $event)"
        />
      </div>
    </div>

    <div v-if="freightsWithoutBolGroups.length">
      <div class="font-weight-500 mb-4 text-grey-navy">{{ t("trip.freights-without-bol") }}</div>

      <div class="d-flex flex-column row-gap-4" data-qa="pod-freights-without-bol">
        <pod-details-card
          v-for="group in freightsWithoutBolGroups"
          :key="group.id"
          :group="group"
          :stop="stop"
          readonly
        />
      </div>
    </div>
  </div>
</template>
